import React, { useState, useEffect, useRef } from "react";
import { func } from "prop-types";
import { Formik, Form, Field } from "formik";
import { Row, Col, Collapse } from "reactstrap";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import queryString from "query-string";
import { FaTimes } from "react-icons/fa";

import {
  Input,
  LabeledDatePicker,
  FieldSpy,
  Select,
} from "../../../../../components/formik";

import {
  riskLevelOptions,
  statusOptions,
  typeOptions,
  MONITORING_QUEUE_ENTRY_TYPES,
  mediaMatchOptions,
  mediaDecisionOptions,
  FILTERS_LABELS,
  INITIAL_FILTERS,
  clientIsActiveOptions,
  TABLE_HEADER_DROPDOWN_VALUES,
  PRESENCE_IN_WATCHLISTS_OPTIONS,
  watchListDecisionOptions,
  watchListDecisionOptionsLabels,
  RegularSearchingCategoryType,
  RegularSearchingCategoryTypeLabels,
} from "../../../constants/monitoringQueueEntryConstants";

import { ButtonWithSpinner } from "../../../../../components/common";
import { createAsyncJob } from "../../../../organization/actions";

import {
  monitoringQueueIsLoadingSelector,
  filtersSelector,
  pageInfoSelector,
  selectSelectedEntries,
  selectDeselectedEntries,
  selectTableHeaderDropdownValue,
  selectTableCheckboxChecked,
  monitoringQueueIsExportLoadingSelector,
} from "../../../selectors";
import {
  setSearchFilters,
  exportMonitoringQueue,
  getMonitoringQueueEntries,
  getMonitoringQueueEntriesCount,
} from "../../../actions";

import AddAsyncJobModal from "../../add-async-job-modal";
import FormikButtonSelect from "../../../../../components/formik/ButtonSelect";
import { CollapseButton, CollapseOpenIcon } from "../../../../../assets";
import Clickable from "../../../../../components/common/Clickable";
import { FiUpload } from "react-icons/fi";
import "./searchForm.css";
import ButtonExport from "../../../../../components/common/ButtonExport/ButtonExport";
import {
  checkTargetType,
  DATE_WITH_CURRENT_YEAR,
  DATE_WITH_MIN_YEAR,
} from "../../../../../utilities/common";
import {
  getFiltersValues,
  shouldShowFiltersLabels,
} from "../../../../../utilities/filters";
import { oidcSelector } from "../../../../../selectors/oidcSelectors";
import useRoles from "../../../../../components/hooks/useRoles";

const SearchMonitoringQueueEntriesForm = ({
  isOpenCollapse,
  toggleCollapse,
  handleSubmit,
  location,
  riskFactors,
  clientTypes,
  monitoringQueueEntries,
  allSelected,
  organizationInfo,
}) => {
  const selectedEntries = useSelector(selectSelectedEntries);
  const deselectedEntries = useSelector(selectDeselectedEntries);
  const tableHeaderDropdownValue = useSelector(selectTableHeaderDropdownValue);
  const tableHeaderCheckboxChecked = useSelector(selectTableCheckboxChecked);
  const isExportLoading = useSelector(monitoringQueueIsExportLoadingSelector);
  const { count } = useSelector(pageInfoSelector);
  const formikRef = useRef(null);
  const [typeOfCheck, setTypeOfCheck] = useState(null);
  const [key, setKey] = useState(typeOfCheck);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [statuses, setStatuses] = useState(statusOptions);
  const { itemsPerPage } = useSelector(pageInfoSelector);
  const [targetType, setTargetType] = useState(null);
  const [rerender, setReRender] = useState(false);
  const oidc = useSelector(oidcSelector);
  const { isClient } = useRoles();
  const canDoMassActions = !!oidc.user.profile.role.find((x) => {
    return (
      x === "coordinator" || x === "manager" || x === "master" || x === "client"
    );
  });

  const getSelectedEntriesCount = () => {
    if (tableHeaderDropdownValue === TABLE_HEADER_DROPDOWN_VALUES.ALL) {
      if (tableHeaderCheckboxChecked) {
        return count - deselectedEntries.length;
      }
      return selectedEntries.length;
    }
    return selectedEntries.length;
  };

  const toggleAdd = () => {
    setIsOpenAdd(!isOpenAdd);
  };

  const hasAdditionalProperties = () => {
    return (
      organizationInfo && organizationInfo.additionalOrganizationProperties
    );
  };

  const dispatch = useDispatch();

  const massActions = (values) => {
    setIsOpenAdd(true);
  };

  const applySelectionPayload = (values) => {
    if (allSelected) {
      return {
        ...values,
        selectedEntries: [],
        deselectedEntries,
        selectAllChecked: true,
      };
    }
    return {
      ...values,
      selectedEntries,
      deselectedEntries: [],
      selectAllChecked: false,
    };
  };

  const handleAddAsyncJob = (values, type) => {
    let valuesCopy = { ...values };
    if (valuesCopy.dateFrom && valuesCopy.dateTo) {
      if (moment(valuesCopy.dateFrom).isSame(valuesCopy.dateTo, "day")) {
        valuesCopy.dateTo = valuesCopy.dateTo.replace("00:00:00", "23:59:59");
      }
    }

    valuesCopy = applySelectionPayload(valuesCopy);

    if (!allSelected && valuesCopy.selectedEntries.length === 0) {
      alert("Please select one or more entries from the list");
      return;
    }

    valuesCopy.jobType = type;

    dispatch(createAsyncJob(valuesCopy));
    toggleAdd();
  };

  const handleExport = (values) => {
    const finalValues = applySelectionPayload(values);

    dispatch(exportMonitoringQueue(finalValues));
  };

  const filters = useSelector(filtersSelector);

  const resetInitialValues = (type) => {
    let cleanCustom = {};

    if (filters.customClientParameters) {
      Object.values(filters.customClientParameters).forEach((l) => {
        l["value"] = undefined;
        cleanCustom[l.name] = undefined;
      });
    }

    return {
      ...INITIAL_FILTERS,
      ...cleanCustom,
      type,
    };
  };

  const clearSingleFilter = (filterKey) => {
    let val = null;
    const currentFilters = { ...filters };

    if (filterKey === "type") {
      clearFilters();
      return;
    }

    if (
      filterKey === "clientName" ||
      filterKey === "clientNumber" ||
      filterKey === "clientOfferId"
    ) {
      val = "";
    }

    if (currentFilters.customClientParameters) {
      const filterPresent = currentFilters.customClientParameters.some(
        (f) => f.name === filterKey,
      );
      if (filterPresent) {
        const customParametersClean =
          currentFilters.customClientParameters.filter(
            (parameter) => parameter.name !== filterKey,
          );

        currentFilters["customClientParameters"] = customParametersClean;
        val = null;
        dispatch(
          setSearchFilters({
            customClientParameters: customParametersClean,
          }),
        );
      }
    }

    dispatch(
      setSearchFilters({
        [filterKey]: val,
      }),
    );

    currentFilters[filterKey] = val;
    formikRef.current.setFieldValue(filterKey, INITIAL_FILTERS[filterKey]);

    if (shouldShowFiltersLabels(currentFilters)) {
      dispatch(getMonitoringQueueEntries({ take: itemsPerPage }));
      dispatch(getMonitoringQueueEntriesCount(currentFilters));
    } else {
      toggleCollapse();
    }

    setReRender(true);
  };

  const { clientNumber, clientOfferId } = queryString.parse(location.search);

  useEffect(() => {
    if (clientNumber && clientOfferId) {
      handleSubmit({ clientNumber, clientOfferId });
    }
  }, [clientNumber, clientOfferId]);

  const isLoading = useSelector(monitoringQueueIsLoadingSelector);

  const handleTypeOfCheckChange = (type) => {
    setTargetType(checkTargetType({ clientTypes, value: type }));
    setTypeOfCheck(type);
    dispatch(setSearchFilters(resetInitialValues(type)));
    setKey(type);
  };

  const changeCustomClientParameter = (props, index, element, e) => {
    if (!props.customClientParameters) {
      props.customClientParameters = [];
    }
    if (!props.customClientParameters[index]) {
      props.customClientParameters[index] = {};
    }

    if (!e) {
      const temp = [...props.customClientParameters];
      const withRemovedEmptyValue = temp.filter((p) => p.name !== element.name);
      props.customClientParameters = withRemovedEmptyValue;
    } else {
      props.customClientParameters[index].name = element.name;
      props.customClientParameters[index].value = e;
      formikRef.current.setFieldValue(element.name, e || null);
    }
  };

  const handleClientTypeChange = (clientType) => {
    setTargetType(checkTargetType({ clientTypes, value: clientType }));
  };

  function clearFilters() {
    setTypeOfCheck(null);
    dispatch(setSearchFilters(resetInitialValues(null)));
    setKey(null);

    formikRef.current.resetForm({
      ...INITIAL_FILTERS,
      hasWatchListMatch: null,
    });

    setReRender(true);
    toggleCollapse();
  }

  const handleRiskLevelChange = (type) => {
    formikRef.current.setFieldValue("scoringStatus", null);
    dispatch(
      setSearchFilters({ ...filters, [INITIAL_FILTERS.scoringStatus]: null }),
    );

    setStatuses(
      statusOptions.filter((a) =>
        type === "High"
          ? a.value === 1 || a.value === 2 || a.value === 3 || a.value === 4
          : type === "Medium"
            ? a.value === 5
            : type === "Normal"
              ? a.value === 6
              : a.value != null,
      ),
    );
  };

  if (rerender) {
    // too many dynamic changes - ugly but easy way to clear what we don't need from prev. render
    setTimeout(() => {
      setReRender(false);
    });
    return null;
  }

  return (
    <Formik
      ref={formikRef}
      onSubmit={handleSubmit}
      initialValues={
        clientNumber && clientOfferId
          ? { clientNumber, clientOfferId }
          : filters
      }
      key={key}
    >
      {(props) => {
        const { values } = props;
        const { dateFrom: dateFromValue, dateTo: dateToValue } = values;

        return (
          <div id="this-div">
            <div>
              <div className="collapse-search">
                <Collapse isOpen={isOpenCollapse}>
                  <Form className="monitoring-queue-search">
                    <div
                      className="monitoring-queue-search-container"
                      style={{ width: 1350 }}
                    >
                      <div>
                        <Field
                          type="text"
                          component={Select}
                          name="type"
                          className="dropdown-group"
                          placeholder="Type of check"
                          options={typeOptions}
                          isClearable
                        />
                        <FieldSpy
                          name="type"
                          onChange={handleTypeOfCheckChange}
                        />
                      </div>

                      <div className="p-0">
                        <Field
                          type="text"
                          name="clientType"
                          className="dropdown-group"
                          placeholder="Client Type"
                          component={FormikButtonSelect}
                          options={clientTypes}
                          isClearable
                        />

                        <FieldSpy
                          name="clientType"
                          onChange={handleClientTypeChange}
                        />
                      </div>

                      <div className="p-0">
                        <Row className="p-0">
                          <Col lg={6} className="p-0 pr-2">
                            <LabeledDatePicker
                              dateFormat="dd.MM.yyyy"
                              name="dateFrom"
                              placeholderText="Date From"
                              label=""
                              minDate={DATE_WITH_MIN_YEAR}
                              maxDate={
                                dateToValue
                                  ? new Date(dateToValue)
                                  : DATE_WITH_CURRENT_YEAR
                              }
                              openToDate={new Date(moment())}
                            />
                          </Col>
                          <Col lg={6} className="p-0 pl-2">
                            <LabeledDatePicker
                              dateFormat="dd.MM.yyyy"
                              name="dateTo"
                              placeholderText="Date To"
                              label=""
                              maxDate={DATE_WITH_CURRENT_YEAR}
                              minDate={
                                dateFromValue
                                  ? new Date(dateFromValue).getTime()
                                  : DATE_WITH_MIN_YEAR
                              }
                              openToDate={new Date(moment())}
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="pl-0">
                        <Field
                          type="text"
                          name="clientOfferId"
                          placeholder="Offer ID"
                          component={Input}
                        />
                      </div>
                      <div className="pr-0">
                        <Field
                          type="text"
                          name="clientNumber"
                          placeholder={
                            targetType === "Company"
                              ? "Company number"
                              : "Client EGN"
                          }
                          component={Input}
                        />
                      </div>
                      <div>
                        <Field
                          type="text"
                          name="clientName"
                          placeholder={
                            targetType === "Company"
                              ? "Company Name"
                              : "Client Name"
                          }
                          component={Input}
                        />
                      </div>
                      {filters.type !== 1 ? (
                        <div className="pb-3 ">
                          <Field
                            type="text"
                            name="IsClientActive"
                            placeholder={"Active"}
                            options={clientIsActiveOptions}
                            component={Select}
                          />
                        </div>
                      ) : null}

                      {filters.type > 2 && filters.type < 7 ? (
                        <RenderOnScoring />
                      ) : null}
                      {typeOfCheck ===
                        MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_INFO ||
                      typeOfCheck ===
                        MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED ? (
                        <RenderOnNegativeMedia />
                      ) : (
                        <RenderOnNotNegativeMedia />
                      )}

                      {filters.type === 2 ? (
                        <RenderOnWatchListRegularSearch />
                      ) : null}

                      {/* special/additional filters */}
                      {hasAdditionalProperties()
                        ? organizationInfo.additionalOrganizationProperties.map(
                            (element, index) => {
                              return (
                                <div>
                                  <Field
                                    key={`${index}-${element.name}`}
                                    type="text"
                                    name={element.name}
                                    placeholder={element.placeholder}
                                    component={Input}
                                  />
                                  <FieldSpy
                                    key={index}
                                    name={element.name}
                                    onChange={(e) =>
                                      changeCustomClientParameter(
                                        props.values,
                                        index,
                                        element,
                                        e.trim(),
                                      )
                                    }
                                  />
                                </div>
                              );
                            },
                          )
                        : null}
                    </div>
                    <Row>
                      <Col
                        lg={12}
                        className=" d-flex justify-content-center pt-4"
                      >
                        <ButtonWithSpinner
                          id="searchMonitoringQueueEntriesSubmitButton"
                          text="Search"
                          outline
                          type="submit"
                          className="search-button-monitoring"
                          loading={isLoading}
                        />
                      </Col>
                    </Row>
                  </Form>
                  <div className="filters-menu-collapse-button ">
                    {monitoringQueueEntries.length ? (
                      <Clickable onClick={toggleCollapse}>
                        <CollapseButton />
                      </Clickable>
                    ) : null}
                  </div>
                </Collapse>
              </div>
              <div className="filters-main-container ">
                <div className="filters-menu-collapse-button">
                  <Clickable onClick={toggleCollapse}>
                    <CollapseOpenIcon />
                  </Clickable>
                </div>

                <Row
                  style={{
                    borderBottom: "1px solid lightGray",
                  }}
                  className="align-items-center"
                >
                  <Col lg={8}>
                    <div className="single-filters-container border-bottom-0 ">
                      {shouldShowFiltersLabels(filters) ? (
                        <div className="filters-menu-label">Filter Applied</div>
                      ) : null}
                      {getFiltersValues(filters).map(([key, value], i) => (
                        <SingleFilter
                          filterKey={key}
                          filterValue={value}
                          clearFilter={() => clearSingleFilter(key)}
                        />
                      ))}

                      {shouldShowFiltersLabels(filters) ? (
                        <div
                          onClick={clearFilters}
                          className="filters-menu-label-clear"
                        >
                          Clear All
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col
                    lg={4}
                    style={{ gap: 14 }}
                    className="d-flex justify-content-end"
                  >
                    <ButtonExport
                      loading={isExportLoading}
                      text="Export"
                      icon={<FiUpload size={20} />}
                      type="button"
                      disabled={isClient}
                      onClick={() => handleExport(props.values)}
                      forced={monitoringQueueEntries.length === 0}
                    />

                    {monitoringQueueEntries.length !== 0 && (
                      <React.Fragment>
                        <ButtonWithSpinner
                          text="Mass Actions"
                          outline
                          type="button"
                          disabled={!canDoMassActions}
                          onClick={() => massActions(props.values)}
                          forced={monitoringQueueEntries.length === 0}
                          className="mass-actions-btn"
                        />
                        <AddAsyncJobModal
                          isOpen={isOpenAdd}
                          toggle={toggleAdd}
                          handleAdd={handleAddAsyncJob}
                          values={props.values}
                        />
                      </React.Fragment>
                    )}
                  </Col>
                </Row>
                <p className="table-checkbox-entries-info">
                  <b>{tableHeaderDropdownValue}: </b>
                  {getSelectedEntriesCount()} selected
                </p>
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );

  function SingleFilter({
    filterKey = "",
    filterValue,
    clearFilter = () => {},
  }) {
    let currentValue = filterValue;

    if (filterKey === "customClientParameters") {
      return null;
    }

    if (filterKey === "type" && filterValue) {
      // set type label
      currentValue = typeOptions.find(
        (type) => type.value === filterValue,
      ).label;
    }

    if (filterKey === "RiskCriteriaName") {
      const label = riskFactors.find((x) => x.value === filterValue).label;
      currentValue = label || "";
    }

    if (filterKey === "scoringStatus") {
      if (statuses) {
        const label = statusOptions.find((x) => x.value === filterValue).label;
        currentValue = label || "";
      }
    }

    if (filterKey === "NegativeMediaDecision") {
      const label = mediaDecisionOptions.find(
        (x) => x.value === filterValue,
      ).label;
      currentValue = label || "";
    }

    if (filterKey === "HasNegativeMediaMatch") {
      if (filterValue === true) {
        currentValue = "YES";
      }

      if (filterValue === false) {
        currentValue = "NO";
      }
    }

    if (filterKey === "RegularSearchingCategoryType") {
      if (filterValue || Number(filterValue) === 0) {
        currentValue = RegularSearchingCategoryTypeLabels[filterValue];
      }
    }

    if (filterKey === "HasNegativeMediaElasticBasedMatch") {
      if (filterValue === true) {
        currentValue = "YES";
      }

      if (filterValue === false) {
        currentValue = "NO";
      }
    }

    if (filterKey === "hasWatchListMatch") {
      if (filterValue === true) {
        currentValue = "YES";
      }

      if (filterValue === false) {
        currentValue = "NO";
      }
    }
    if (filterKey === "IsClientActive") {
      if (filterValue === true) {
        currentValue = "YES";
      }

      if (filterValue === false) {
        currentValue = "NO";
      }
    }

    if (filterKey === "IsClientActive") {
      if (filterValue === true) {
        currentValue = "YES";
      }
      if (filterValue === false) {
        currentValue = "NO";
      }
    }

    if (filterKey === "WatchListSearchingDecisionTypeFilter") {
      if (filterValue === 0) {
        currentValue = watchListDecisionOptionsLabels.PENDING;
      }

      if (filterValue === 1) {
        currentValue = watchListDecisionOptionsLabels.FALSE_POSITIVE;
      }

      if (filterValue === 2) {
        currentValue = watchListDecisionOptionsLabels.TRUE_MATCH;
      }
    }

    if (filterKey === "dateFrom" || (filterKey === "dateTo" && filterValue)) {
      // format date
      currentValue = moment(new Date(filterValue)).format("DD.MM.yyyy");
    }

    return (
      <div className="single-filter-box">
        <div className="single-filter-label">
          {FILTERS_LABELS[filterKey] || `${filterKey}:`}
        </div>
        <div className="single-filter-chip">
          {currentValue}
          {filterKey !== "clientType" ? (
            <FaTimes
              className="remove-filter-icon"
              onClick={clearFilter}
              color="#707070"
            />
          ) : null}
        </div>
      </div>
    );
  }

  function RenderOnScoring() {
    if (!typeOfCheck) {
      return null;
    }
    return (
      <React.Fragment>
        <div>
          <Field
            type="text"
            component={Select}
            name="riskLevel"
            placeholder="Result / RISK LEVEL"
            className="dropdown-group"
            options={riskLevelOptions}
            isClearable
          />
          <FieldSpy name="riskLevel" onChange={handleRiskLevelChange} />
        </div>
        <div className="pb-3">
          <Field
            type="text"
            component={Select}
            name="scoringStatus"
            className="dropdown-group"
            placeholder="Scoring status"
            options={statuses}
            isClearable
          />
        </div>

        <div>
          <Field
            type="text"
            component={Select}
            name="RiskCriteriaName"
            className="dropdown-group"
            placeholder="Risk criteria type"
            options={riskFactors}
            isClearable
          />
        </div>

        <div>
          <Field
            type="text"
            name="RiskCriteriaValue"
            placeholder="Please manually enter the risk criteria value here in order to search"
            component={Input}
          />
        </div>
      </React.Fragment>
    );
  }

  function RenderOnNotNegativeMedia() {
    if (!typeOfCheck) {
      return null;
    }

    return (
      <div>
        <Field
          name="hasWatchListMatch"
          placeholder="Presence in Watchlists"
          className="dropdown-group"
          component={Select}
          options={PRESENCE_IN_WATCHLISTS_OPTIONS}
          isClearable
        />
      </div>
    );
  }

  function RenderOnWatchListRegularSearch() {
    if (!typeOfCheck) {
      return null;
    }

    return (
      <React.Fragment>
        <div>
          <Field
            name="WatchListSearchingDecisionTypeFilter"
            placeholder="Watchlist Decision"
            className="dropdown-group"
            component={Select}
            options={watchListDecisionOptions}
            isClearable
          />
        </div>
        <div className="pb-3">
          <Field
            name="RegularSearchingCategoryType"
            placeholder="Watchlist Types"
            className="dropdown-group"
            component={Select}
            options={RegularSearchingCategoryType}
            isClearable
          />
        </div>
      </React.Fragment>
    );
  }

  // categoryType - за детайлити
  // type of check - 1vata kolona
  //
  function RenderOnNegativeMedia() {
    return (
      <React.Fragment>
        <div>
          {typeOfCheck ===
          MONITORING_QUEUE_ENTRY_TYPES.NEGATIVE_MEDIA_ELASTIC_BASED ? (
            <Field
              type="text"
              name="HasNegativeMediaElasticBasedMatch"
              placeholder="Presence in Media"
              component={Select}
              options={mediaMatchOptions}
              isClearable
            />
          ) : (
            <Field
              type="text"
              name="HasNegativeMediaMatch"
              placeholder="Presence in Media"
              component={Select}
              options={mediaMatchOptions}
              isClearable
            />
          )}
        </div>
        <div className="pb-3">
          <Field
            type="text"
            name="NegativeMediaDecision"
            placeholder="Decision"
            component={Select}
            options={mediaDecisionOptions}
            isClearable
          />
        </div>
      </React.Fragment>
    );
  }
};

SearchMonitoringQueueEntriesForm.propTypes = {
  handleSubmit: func.isRequired,
};

export default SearchMonitoringQueueEntriesForm;



// WEBPACK FOOTER //
// ./src/features/monitoring-queue-entry/components/searching/search-form/SearchMonitoringQueueEntriesForm.jsx